import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useDetailBreadcrumbLabel } from "@/contexts/DetailBreadcrumbContext";

export interface BreadcrumbEntry {
  label: string;
  /** Sem href o nível é exibido como texto, sem navegação. */
  href?: string;
}

const COLETAS: BreadcrumbEntry = { label: "Coletas", href: "/coletas" };
const FINANCEIRO: BreadcrumbEntry = { label: "Financeiro", href: "/financeiro" };
const AJUDA: BreadcrumbEntry = { label: "Central de Ajuda", href: "/ajuda" };

/** Rota do detalhamento de fatura/medição — o segmento final é completado em `useBreadcrumb`. */
const FINANCEIRO_DETALHE_RULE = /^\/financeiro\/[^/]+$/;

interface BreadcrumbRule {
  test: RegExp;
  build: (match: RegExpMatchArray) => BreadcrumbEntry[];
}

/** Avaliadas em ordem — a primeira que casar vence. */
const BREADCRUMB_RULES: BreadcrumbRule[] = [
  {
    test: /^\/(dashboard)?$/,
    build: () => [{ label: "Dashboards" }, { label: "Operacional" }],
  },
  {
    test: /^\/indicadores-ambientais$/,
    build: () => [{ label: "Dashboards" }, { label: "Indicador Ambiental" }],
  },
  {
    test: /^\/(dadosimpacto|dadosdeimpactojadlog)$/,
    build: () => [{ label: "Dashboards" }, { label: "Dados de Impacto" }],
  },
  {
    test: /^\/coletas$/,
    build: () => [{ label: "Operacional" }, { label: "Coletas" }],
  },
  {
    test: /^\/coleta-sob-demanda\/nova$/,
    build: () => [
      { label: "Operacional" },
      COLETAS,
      { label: "Nova solicitação" },
    ],
  },
  {
    test: /^\/coleta-sob-demanda\/[^/]+$/,
    build: () => [{ label: "Operacional" }, COLETAS, { label: "Detalhe" }],
  },
  {
    test: /^\/financeiro$/,
    build: () => [{ label: "Operacional" }, { label: "Financeiro" }],
  },
  {
    // Sem esta regra o detalhe da medição caía no fallback de primeiro segmento, que produz
    // só "Financeiro" — o header perdia o nível atual, e a trilha acabou duplicada dentro da
    // página para compensar. O header é o lugar dela, como nas outras telas.
    //
    // "Detalhes" é o fallback estático: o header completa com "Fatura"/"Medição" assim que a
    // página carrega o registro (ver `useBreadcrumb`).
    test: FINANCEIRO_DETALHE_RULE,
    build: () => [{ label: "Operacional" }, FINANCEIRO, { label: "Detalhes" }],
  },
  { test: /^\/configuracoes$/, build: () => [{ label: "Configurações" }] },
  {
    // GR-668: engrenagem > Usuários direto — sem Configurações como pai,
    // que deixou de ser o ponto de entrada (mesmo padrão de /meu-perfil).
    test: /^\/(gestao-usuario|usuarios)$/,
    build: () => [{ label: "Usuários" }],
  },
  { test: /^\/meu-perfil$/, build: () => [{ label: "Minha conta" }] },
  { test: /^\/ajuda$/, build: () => [{ label: "Central de Ajuda" }] },
  {
    test: /^\/ajuda\/([^/]+)$/,
    build: (match) => [AJUDA, { label: humanize(match[1]) }],
  },
  {
    test: /^\/perguntasfrequentes(\/([^/]+))?$/,
    build: (match) =>
      match[2]
        ? [
            { label: "Perguntas Frequentes", href: "/perguntasfrequentes" },
            { label: humanize(match[2]) },
          ]
        : [{ label: "Perguntas Frequentes" }],
  },
  { test: /^\/suporte$/, build: () => [{ label: "Suporte" }] },
  { test: /^\/csat$/, build: () => [{ label: "CSAT" }] },
  { test: /^\/notificacoes$/, build: () => [{ label: "Notificações" }] },
  {
    test: /^\/termosdeuso(musa)?$/,
    build: () => [{ label: "Termos de Uso" }],
  },
  { test: /^\/send-mail$/, build: () => [{ label: "Envio de E-mails" }] },
  {
    test: /^\/bulk-update$/,
    build: () => [{ label: "Atualização em Lote" }],
  },
];

const humanize = (segment: string): string => {
  const text = decodeURIComponent(segment).replace(/[-_]/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const buildBreadcrumb = (pathname: string): BreadcrumbEntry[] => {
  for (const rule of BREADCRUMB_RULES) {
    const match = pathname.match(rule.test);
    if (match) return rule.build(match);
  }

  const [firstSegment] = pathname.split("/").filter(Boolean);
  return firstSegment ? [{ label: humanize(firstSegment) }] : [];
};

export const useBreadcrumb = (): BreadcrumbEntry[] => {
  const { pathname } = useLocation();
  const detailLabel = useDetailBreadcrumbLabel();

  return useMemo(() => {
    const entries = buildBreadcrumb(pathname);
    if (!detailLabel || !FINANCEIRO_DETALHE_RULE.test(pathname)) return entries;

    const lastIndex = entries.length - 1;
    return entries.map((entry, index) =>
      index === lastIndex ? { ...entry, label: `Detalhes ${detailLabel}` } : entry,
    );
  }, [pathname, detailLabel]);
};
